<script setup lang="ts">
import type { RegisterUser } from '~/models/User/Register'
import { useI18n } from 'vue-i18n'
import { computed, ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useAuthStore } from '~/stores/auth'
import { ColorName } from '~/models/Content/Color'
import { useAppColor } from '~/composables/useAppColor'
import Button from '~/components/ui/Button.vue'
import AuCheckbox from '~/components/ui/AuCheckbox.vue'
import LoggedInCallout from '~/components/register/LoggedInCallout.vue'
import InputError from '~/components/register/InputError.vue'
import ErrorCallout from '~/components/register/ErrorCallout.vue'

const props = withDefaults(defineProps<{
  isLoading?: boolean
  errorStatus?: number
}>(), {
  isLoading: false,
  errorStatus: 0,
})

const emit = defineEmits(['register', 'cancel'])

const modelValue = defineModel<RegisterUser>({ default: {} })

const { t } = useI18n()
const { isAuthenticated } = storeToRefs(useAuthStore())
useAppColor(ColorName.Gray5)

const firstNameBlurred = ref(false)
const lastNameBlurred = ref(false)
const emailBlurred = ref(false)

const termsUrl = import.meta.env.VITE_AUNIVERS_PRIVACY

const isEmailValid = computed(() => /^[^\s,@]*@[^\s,][^\s,.]*\.[^\s,]+$/.test(modelValue.value.email))
const isEmailLength = computed(() => modelValue.value.email.length >= 4 && modelValue.value.email.length <= 500)
const isFirstNameValid = computed(() => modelValue.value.firstName.length >= 2)
const isLastNameValid = computed(() => modelValue.value.lastName.length >= 2)
const canInput = computed(() => !props.isLoading && !isAuthenticated.value)

const canSubmit = computed(() => canInput.value
  && isEmailValid.value
  && isEmailLength.value
  && isFirstNameValid.value
  && isLastNameValid.value
  && modelValue.value.terms
)
</script>

<template>
  <div class="mb-6 space-y-4 text-center">
    <h1
      class="text-4xl font-semibold text-gray-50"
      v-text="t('register.form.title')"
    />
    <p
      v-if="!isAuthenticated"
      class="text-gray-40"
      v-text="t('register.form.intro')"
    />
  </div>
  <ErrorCallout
    v-if="props.errorStatus > 0"
    :status-code="props.errorStatus"
  />
  <LoggedInCallout v-if="isAuthenticated" />
  <form class="flex flex-col gap-5">
    <label>
      <span
        class="mb-2 inline-block"
        v-text="t('register.input.firstName')"
      />
      <input
        id="firstName"
        v-model="modelValue.firstName"
        autocomplete="given-name"
        type="text"
        :disabled="!canInput"
        class="border-gray-10 block w-full rounded-xl border bg-white px-4 py-2 transition focus-visible:ring disabled:cursor-not-allowed"
        @blur="firstNameBlurred = true"
        @input="firstNameBlurred = false"
      >
    </label>
    <InputError
      v-if="!isFirstNameValid && firstNameBlurred && modelValue.firstName"
      :text="t('register.invalid.firstName')"
    />
    <label>
      <span
        class="mb-2 inline-block"
        v-text="t('register.input.lastName')"
      />
      <input
        id="lastName"
        v-model="modelValue.lastName"
        autocomplete="family-name"
        type="text"
        :disabled="!canInput"
        class="border-gray-10 block w-full rounded-xl border bg-white px-4 py-2 transition focus-visible:ring disabled:cursor-not-allowed"
        @blur="lastNameBlurred = true"
        @input="lastNameBlurred = false"
      >
    </label>
    <InputError
      v-if="!isLastNameValid && lastNameBlurred && modelValue.lastName"
      :text="t('register.invalid.lastName')"
    />
    <label>
      <span
        class="mb-2 inline-block"
        v-text="t('register.input.email')"
      />
      <input
        id="email"
        v-model="modelValue.email"
        type="email"
        :disabled="!canInput"
        class="border-gray-10 block w-full rounded-xl border bg-white px-4 py-2 transition focus-visible:ring disabled:cursor-not-allowed"
        @blur="emailBlurred = true"
        @input="emailBlurred = false"
      >
    </label>
    <InputError
      v-if="!(isEmailValid && isEmailLength) && emailBlurred && modelValue.email"
      :text="t('register.invalid.email')"
    />
    <label class="flex items-center gap-2">
      <AuCheckbox
        v-model="modelValue.terms"
        :disabled="!canInput"
      /> <span
        class="au-cms"
        v-html="t('register.input.terms', { url: termsUrl })"
      />
    </label>
  </form>
  <div class="mt-10 flex justify-end gap-4">
    <Button
      href="/"
      variant="outline"
      size="lg"
      :disabled="isLoading"
    >
      {{ t('cancel') }}
    </Button>
    <Button
      size="lg"
      :disabled="!canSubmit"
      @click.prevent="emit('register', modelValue)"
    >
      {{ t('register.form.submit') }}
    </Button>
  </div>
</template>
